import type { PlaidLinkOnEventMetadata } from 'react-plaid-link';
import type { Deprecated_HandleItemUpdate } from 'src/types/account';
import { useState } from 'react';
import { Button } from '@savvy-kit/components/button';
import { cn } from '@savvy-kit/lib/utils';
import { ArrowLeft, ChevronRight, Pencil } from 'lucide-react';
import LiabilityForm from 'src/components/liability/liability-form';
import { ItemType } from 'src/constants/account-link';
import { PlaidErrorCode } from 'src/types/plaid';

import type { PlaidProduct } from '@savvy/constants/generated/gql-enums';
import { PLAID_PRODUCT_INVESTMENTS, PLAID_PRODUCT_TRANSACTIONS } from '@savvy/constants/plaid-products';

import { AggregatorConnectButton } from '../../aggregator/aggregator-connect-button';
import AddEditRealEstate from '../../asset/add-edit-real-estate';
import AssetForm from '../../asset/asset-form';
import AddEditCompanyEquity from '../../company-equity/add-edit-company-equity';
import FinancialAccountForm from '../../financial-account/financial-account-form';

function getTitle(itemType: ItemType) {
  switch (itemType) {
    case ItemType.BANK_ACCOUNT:
      return 'Add a bank account';
    case ItemType.INVESTMENT_ACCOUNT:
      return 'Add an investment account';
    case ItemType.REAL_ESTATE:
      return 'Real estate details';
    case ItemType.OTHER_ACCOUNT:
      return 'Add Account';
    case ItemType.OTHER_ASSET:
      return 'Add Asset';
    case ItemType.OTHER_LIABILITY:
      return 'Add Liability';
    case ItemType.COMPANY_EQUITY:
    default:
      return 'Add Account';
  }
}

function getProducts(itemType: ItemType): PlaidProduct[] {
  switch (itemType) {
    case ItemType.BANK_ACCOUNT:
      return [PLAID_PRODUCT_TRANSACTIONS];
    case ItemType.INVESTMENT_ACCOUNT:
      return [PLAID_PRODUCT_INVESTMENTS, PLAID_PRODUCT_TRANSACTIONS];
    case ItemType.REAL_ESTATE:
    case ItemType.COMPANY_EQUITY:
    case ItemType.OTHER_ACCOUNT:
    case ItemType.OTHER_ASSET:
    case ItemType.OTHER_LIABILITY:
    default:
      return [PLAID_PRODUCT_TRANSACTIONS];
  }
}

function isManualOnlyType(itemType: ItemType) {
  return [ItemType.REAL_ESTATE].includes(itemType);
}

type AddItemContentWrapperProps = {
  readonly onSuccess: Deprecated_HandleItemUpdate;
  readonly itemType: ItemType;
  readonly clientUserId?: string;
  readonly hiddenFromClient?: boolean;
  readonly setItemType: (itemType?: ItemType) => void;
};

export default function AddItemContentWrapper({
  onSuccess,
  itemType,
  clientUserId,
  hiddenFromClient,
  setItemType,
}: AddItemContentWrapperProps) {
  const [enterManually, setEnterManually] = useState(hiddenFromClient);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [isConnectingPlaidAccount, setIsConnectingPlaidAccount] = useState(false);

  const showManualEntry =
    enterManually || [ItemType.OTHER_ACCOUNT, ItemType.OTHER_ASSET, ItemType.OTHER_LIABILITY].includes(itemType);

  if (itemType === ItemType.REAL_ESTATE) {
    return (
      <AddEditRealEstate
        clientUserId={clientUserId}
        hiddenFromClient={hiddenFromClient}
        onSuccess={onSuccess}
        onClickBack={() => {
          setItemType(undefined);
        }}
      />
    );
  } else if (itemType === ItemType.COMPANY_EQUITY) {
    return (
      <AddEditCompanyEquity
        onSuccess={onSuccess}
        clientUserId={clientUserId}
        hiddenFromClient={hiddenFromClient}
        onClickBack={() => {
          setItemType(undefined);
        }}
      />
    );
  }

  return (
    <>
      {/* Floats at the top-left of the dialog paper to match the legacy
          ItemDialogBackButton placement (the AddItemDialog DialogTitle reserves
          `pb: 8.5` of vertical room for it when an itemType is selected). */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          setItemType(undefined);
        }}
        className="text-muted-foreground absolute top-6 left-6"
        aria-label="Go back"
        icon={<ArrowLeft className="size-4" />}
      >
        Back
      </Button>
      <div className="flex flex-col">
        <h2 className="text-dark mb-6 text-lg font-semibold">{getTitle(itemType)}</h2>
        {!showManualEntry && (
          <>
            {[ItemType.BANK_ACCOUNT, ItemType.INVESTMENT_ACCOUNT].includes(itemType) && !hiddenFromClient && (
              <div className="bg-dark text-light rounded-md">
                <AggregatorConnectButton
                  products={getProducts(itemType)}
                  title="Connect your accounts"
                  subtitle="Keep your account balance and details automatically up to date."
                  onSuccess={onSuccess}
                  onPlaidError={(metadata: PlaidLinkOnEventMetadata) => {
                    if (metadata.error_code === PlaidErrorCode.MFA_NOT_SUPPORTED) {
                      setEnterManually(true);
                      setErrorMessage(
                        `Sorry, Plaid does not support ${metadata.institution_name} with multi-factor authentication. Please enter the account value manually.`,
                      );
                    }
                  }}
                  clientUserId={clientUserId}
                  setIsLoading={setIsConnectingPlaidAccount}
                />
              </div>
            )}
            {!isManualOnlyType(itemType) && (
              <Button
                variant="secondary"
                onClick={() => {
                  if (!isConnectingPlaidAccount) {
                    setEnterManually(true);
                  }
                }}
                disabled={isConnectingPlaidAccount}
                className={cn(
                  // Vertical proportions tuned to mirror the legacy MUI version
                  // (my:4 gap / py:2 / subtitle1 / fontSize="large" chevron) so
                  // this card visually pairs with the dark "Connect your
                  // accounts" CTA immediately above it.
                  'mt-8 h-auto min-h-[68px] w-full justify-between gap-3 rounded-md px-4 py-4 text-left',
                  isConnectingPlaidAccount && 'opacity-30',
                )}
                aria-label="Enter details manually"
              >
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <Pencil className="text-dark size-6 shrink-0" />
                  <span className="text-dark text-base font-medium">Enter details manually</span>
                </div>
                <ChevronRight className="text-dark size-8 shrink-0" />
              </Button>
            )}
          </>
        )}
        {showManualEntry && (
          <>
            {[ItemType.OTHER_ASSET].includes(itemType) && (
              <AssetForm onSuccess={onSuccess} clientUserId={clientUserId} hiddenFromClient={hiddenFromClient} />
            )}
            {itemType === ItemType.OTHER_LIABILITY && (
              <LiabilityForm
                isEditMode={false}
                onSuccess={onSuccess}
                hiddenFromClient={hiddenFromClient}
                clientUserId={clientUserId}
              />
            )}
            {[ItemType.OTHER_ACCOUNT, ItemType.BANK_ACCOUNT, ItemType.INVESTMENT_ACCOUNT].includes(itemType) && (
              <FinancialAccountForm
                errorMessage={errorMessage}
                onSuccess={onSuccess}
                itemType={itemType}
                hiddenFromClient={hiddenFromClient}
                clientUserId={clientUserId}
              />
            )}
          </>
        )}
      </div>
    </>
  );
}
